import React, { useState, useEffect } from 'react';
import { questionnaireService } from '../services/questionnaireService';
import { useToast } from '../context/ToastContext';
import RichTextEditor from './RichTextEditor';

interface ConsentConfigTabProps {
  questionnaireId: number;
  questionnaire: any;
  onUpdate: () => void;
}

const ConsentConfigTab: React.FC<ConsentConfigTabProps> = ({
  questionnaireId,
  questionnaire,
  onUpdate
}) => {
  const { showToast } = useToast();
  const [consentRequired, setConsentRequired] = useState(false);
  const [signatureRequired, setSignatureRequired] = useState(false);
  const [consentContent, setConsentContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // 初始化数据
  useEffect(() => {
    if (questionnaire) {
      setConsentRequired(questionnaire.consentRequired || false);
      setSignatureRequired(questionnaire.signatureRequired || false);
      setConsentContent(questionnaire.consentContent || '');
    }
  }, [questionnaire]);

  // 检测变化
  useEffect(() => {
    if (questionnaire) {
      const changed =
        consentRequired !== (questionnaire.consentRequired || false) ||
        signatureRequired !== (questionnaire.signatureRequired || false) ||
        consentContent !== (questionnaire.consentContent || '');
      setHasChanges(changed);
    }
  }, [consentRequired, signatureRequired, consentContent, questionnaire]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // 验证：签字要求必须配合知情同意书
      if (signatureRequired && !consentRequired) {
        showToast('要求签字时必须启用知情同意书', 'error');
        return;
      }

      // 验证：启用知情同意书时必须有内容
      if (consentRequired && !consentContent.trim()) {
        showToast('请输入知情同意书内容', 'warning');
        return;
      }

      await questionnaireService.updateQuestionnaire(questionnaireId, {
        title: questionnaire.title,
        description: questionnaire.description,
        consentRequired,
        signatureRequired,
        consentContent: consentRequired ? consentContent : ''
      });

      showToast('知情同意书配置已保存', 'success');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to save consent config:', error);
      showToast('保存失败，请重试', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (questionnaire) {
      setConsentRequired(questionnaire.consentRequired || false);
      setSignatureRequired(questionnaire.signatureRequired || false);
      setConsentContent(questionnaire.consentContent || '');
      setHasChanges(false);
    }
  };

  const insertTemplate = () => {
    const template = `<h2>知情同意书</h2>
<p>尊敬的参与者：</p>
<p>感谢您参加本次问卷调查。在填写问卷前，请仔细阅读以下内容：</p>

<h3>一、研究目的</h3>
<p>本次调查旨在了解[调查目的]，为[相关工作]提供参考依据。</p>

<h3>二、知情同意要点</h3>
<ul>
  <li><strong>自愿参与</strong>：您的参与完全是自愿的，您有权在任何时候退出本次调查</li>
  <li><strong>保密性</strong>：您的所有回答将被严格保密，仅用于学术研究</li>
  <li><strong>数据使用</strong>：调查数据将仅用于统计分析，不会泄露您的个人信息</li>
  <li><strong>风险提示</strong>：本调查不涉及任何风险，如有不适可随时停止</li>
</ul>

<h3>三、您的权利</h3>
<p>您有权：</p>
<ul>
  <li>了解本研究的目的和过程</li>
  <li>拒绝回答任何问题</li>
  <li>随时退出本次调查</li>
</ul>

<p><em>如果您同意参与本次调查，请在下方签名并点击"同意并继续"按钮。</em></p>
<p>感谢您的配合与支持！</p>`;
    setConsentContent(template);
  };

  return (
    <div className="space-y-6">
      {/* 说明卡片 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">关于知情同意书</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>知情同意书用于告知参与者研究目的、数据使用方式、隐私保护等内容，确保参与者充分了解并自愿参与调查。</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>启用后，用户在答题前必须先阅读并同意知情同意书</li>
                <li>可选择是否要求用户手写签字确认</li>
                <li>系统会自动记录用户的同意时间、IP地址和签字数据</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 配置选项 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">基本设置</h3>

        <div className="space-y-4">
          {/* 启用知情同意书 */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="consentRequired"
                checked={consentRequired}
                onChange={(e) => {
                  setConsentRequired(e.target.checked);
                  if (!e.target.checked) {
                    setSignatureRequired(false);
                  }
                }}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="consentRequired" className="font-medium text-gray-700 cursor-pointer">
                启用知情同意书
              </label>
              <p className="text-sm text-gray-500">启用后，用户在答题前需要先阅读并同意知情同意书</p>
            </div>
          </div>

          {/* 要求用户签字 */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="signatureRequired"
                checked={signatureRequired}
                disabled={!consentRequired}
                onChange={(e) => setSignatureRequired(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="signatureRequired"
                className={`font-medium ${consentRequired ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
              >
                要求用户签字
              </label>
              <p className="text-sm text-gray-500">启用后，用户需要使用手写签字板进行签名确认</p>
            </div>
          </div>
        </div>
      </div>

      {/* 知情同意书内容编辑 */}
      {consentRequired && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">知情同意书内容</h3>
            <button
              type="button"
              onClick={insertTemplate}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              插入模板
            </button>
          </div>

          <div className="mb-4">
            <RichTextEditor
              value={consentContent}
              onChange={setConsentContent}
              placeholder="请输入知情同意书内容，建议包括：研究目的、保密承诺、参与者权利等..."
            />
          </div>

          <p className="text-xs text-gray-500">
            💡 提示：支持富文本格式（标题、粗体、列表等），用户将在答题前看到此内容
          </p>
        </div>
      )}

      {/* 预览区域 */}
      {consentRequired && consentContent && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">预览效果</h3>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: consentContent }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            这是用户将看到的实际效果（样式可能与最终展示略有差异）
          </p>
        </div>
      )}

      {/* 保存按钮 */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="text-sm text-gray-600">
          {hasChanges && (
            <span className="text-yellow-600 font-medium">● 有未保存的更改</span>
          )}
        </div>
        <div className="flex gap-3">
          {hasChanges && (
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              取消
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? '保存中...' : '保存配置'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentConfigTab;
